import { useState, useEffect, useContext } from "react";
import {
  CheckCircle2,
  Circle,
  FileSignature,
  Vote,
  ClipboardList,
  ClipboardCheck,
  HeartPulse,
  Users,
  ArrowRight,
  Clock,
  ChevronDown,
  ChevronUp,
  BarChart3,
  CheckSquare,
  Check,
  X,
  Shield,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { cn } from "../../lib/utils";
import PulseRequestApproval from "./PulseRequestApproval";
import {
  getPendingApprovals,
  approveBoardMember,
  rejectBoardMember,
  fetchBoardActions,
  markBoardActionComplete,
  decidePulseRequest,
} from "../../services/businessDashApi";
import {
  dispatchBusinessDashNavigate,
  meetingFolderForActionType,
  setSessionChemistryNotificationId,
  setSessionMeetingFocus,
  setSessionStrategyAttentionId,
  toNullableNumber,
} from "../../lib/boardActionsNavigate";
import { UserModulesContext } from "../../../../components/UserModuleContext/index";
import jwtDecode from "jwt-decode";

// ─── Helpers ────────────────────────────────────────────────────────────────

const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return "current-user";
    const decoded = jwtDecode(token);
    return String(decoded.user_id || decoded.sub || "current-user");
  } catch {
    return "current-user";
  }
};

const getEstimatedTime = (type) => {
  const map = {
    "governance-pulse": 20,
    "survey-response": 20,
    chemistry: 15,
    "review-board-results": 10,
    "agenda-review": 10,
    voting: 5,
    signature: 3,
    "pulse-request": 2,
  };
  return map[type] ?? 10;
};

const getActionCategory = (type) => {
  switch (type) {
    case "voting":
      return "decisions";
    case "governance-pulse":
    case "chemistry":
    case "survey-response":
    case "review-board-results":
    case "pulse-request":
      return "assessments";
    default:
      return "meetings";
  }
};

/** API uses `president`; some code paths use `chairperson`. */
const isChairRole = (role) => {
  if (role == null || role === "") return false;
  const r = String(role).toLowerCase().replace(/\s+/g, "-");
  return r === "chairperson" || r === "president";
};

/** Chair, secretary, or admin — same visibility as Governance Approvals in design spec. */
const canSeeGovernanceMemberApprovals = (role) => {
  if (role == null || role === "") return false;
  const r = String(role).toLowerCase().replace(/\s+/g, "-");
  return (
    isChairRole(r) ||
    r === "secretary" ||
    r === "admin" ||
    r === "administrator"
  );
};

const isChair = (role) => isChairRole(role);

/** Sample governance rows — only shown when inbox actions are in demo mode and the API returns no pending members. IDs must use prefix `sample-gov-` so approve/reject skips real API calls. */
const buildMockApprovalRequests = () => [
  {
    id: "sample-gov-1",
    name: "Lisa Martinez",
    email: "lisa.martinez@company.com",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    accessType: "member",
    requestedAt: new Date(Date.now() - 172800000),
    accessManagerApproved: true,
  },
  {
    id: "sample-gov-2",
    name: "David Wilson",
    email: "david.wilson@company.com",
    image:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop&crop=face",
    accessType: "administrator",
    requestedAt: new Date(Date.now() - 432000000),
    accessManagerApproved: false,
  },
  {
    id: "sample-gov-3",
    name: "Amira Hassan",
    email: "amira.hassan@company.com",
    accessType: "member",
    requestedAt: new Date(Date.now() - 3600000 * 8),
    accessManagerApproved: true,
  },
];

// ─── Mock fallback data ──────────────────────────────────────────────────────

const buildMockActions = (currentUserId) => [
  {
    id: "1",
    type: "governance-pulse",
    title: "Governance Pulse Assessment",
    description: "Complete the quarterly governance assessment",
    assigneeId: currentUserId,
    dueDate: "2026-02-05",
    status: "pending",
    priority: "high",
    estimatedMinutes: getEstimatedTime("governance-pulse"),
    category: "assessments",
  },
  {
    id: "2",
    type: "chemistry",
    title: "Board Chemistry Review",
    description: "Participate in board dynamics evaluation",
    assigneeId: currentUserId,
    dueDate: "2026-02-10",
    status: "pending",
    priority: "medium",
    estimatedMinutes: getEstimatedTime("chemistry"),
    category: "assessments",
  },
  {
    id: "3",
    type: "agenda-review",
    title: "Q1 Board Meeting Agenda",
    description: "Review and confirm meeting agenda items",
    assigneeId: currentUserId,
    relatedMeeting: "Q1 Board Meeting",
    status: "in-progress",
    priority: "high",
    estimatedMinutes: getEstimatedTime("agenda-review"),
    category: "meetings",
  },
  {
    id: "4",
    type: "voting",
    title: "Budget Approval Vote",
    description: "Cast your vote on the 2026 budget proposal",
    assigneeId: currentUserId,
    dueDate: "2026-01-30",
    status: "pending",
    priority: "high",
    estimatedMinutes: getEstimatedTime("voting"),
    category: "decisions",
  },
  {
    id: "5",
    type: "signature",
    title: "Q4 Meeting Minutes",
    description: "Sign the approved meeting minutes",
    assigneeId: currentUserId,
    relatedMeeting: "Q4 Board Meeting",
    status: "pending",
    priority: "medium",
    estimatedMinutes: getEstimatedTime("signature"),
    category: "meetings",
  },
  {
    id: "6",
    type: "review-board-results",
    title: "Review Board Chemistry Results",
    description:
      "Review aggregated survey results and prepare chair summary",
    assigneeId: "chair-placeholder",
    dueDate: "2026-02-15",
    status: "pending",
    priority: "high",
    estimatedMinutes: getEstimatedTime("review-board-results"),
    surveyType: "chemistry",
    surveyRunId: "survey-run-001",
    category: "assessments",
  },
  {
    id: "7",
    type: "pulse-request",
    title: "Governance Pulse anmodning",
    description:
      "Godkend, udsæt eller afvis den planlagte Governance Pulse vurdering",
    assigneeId: "chair-placeholder",
    status: "pending",
    priority: "medium",
    estimatedMinutes: getEstimatedTime("pulse-request"),
    category: "assessments",
    pulseRequestId: "pulse-req-001",
  },
  {
    id: "8",
    type: "survey-response",
    title: "Strategy workspace follow-up",
    description: "Respond to the flagged attention item from the last strategy review",
    assigneeId: currentUserId,
    dueDate: "2026-02-20",
    status: "pending",
    priority: "medium",
    estimatedMinutes: getEstimatedTime("survey-response"),
    category: "assessments",
    attentionItemId: 101,
  },
  {
    id: "9",
    type: "voting",
    title: "Audit committee charter update",
    description: "Approve the revised charter before the next annual meeting",
    assigneeId: currentUserId,
    dueDate: "2026-02-28",
    status: "pending",
    priority: "low",
    estimatedMinutes: getEstimatedTime("voting"),
    category: "decisions",
  },
  {
    id: "10",
    type: "agenda-review",
    title: "Emergency board session — agenda pack",
    description: "Review materials and confirm you can attend on short notice",
    assigneeId: currentUserId,
    relatedMeeting: "Emergency session",
    dueDate: "2026-02-08",
    status: "pending",
    priority: "high",
    estimatedMinutes: getEstimatedTime("agenda-review"),
    category: "meetings",
  },
  {
    id: "11",
    type: "governance-pulse",
    title: "ESG oversight checklist",
    description: "Short pulse on environmental and social risk oversight",
    assigneeId: currentUserId,
    dueDate: "2026-03-01",
    status: "pending",
    priority: "low",
    estimatedMinutes: getEstimatedTime("governance-pulse"),
    category: "assessments",
  },
  {
    id: "12",
    type: "signature",
    title: "Prior year annual report — director acknowledgment",
    description: "Electronic sign-off on the published annual report",
    assigneeId: currentUserId,
    relatedMeeting: "AGM 2025",
    status: "completed",
    priority: "low",
    estimatedMinutes: getEstimatedTime("signature"),
    category: "meetings",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const BoardActionsPanel = ({
  onNavigateToGovernance,
  onNavigateToMeeting,
  onNavigateToChairSummary,
  onMarkResultsReviewed,
  onMemberApproved,
  onPulseRequestDecision,
}) => {
  const { boardMemberRole } = useContext(UserModulesContext);
  const currentUserRole = boardMemberRole || "ordinary";
  const currentUserId = getCurrentUserId();

  const [isExpanded, setIsExpanded] = useState(false);
  const [pulseRequestDialogOpen, setPulseRequestDialogOpen] = useState(false);
  const [selectedPulseRequest, setSelectedPulseRequest] = useState(null);
  const [actions, setActions] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  /** True when the last fetch returned [] or failed — we load sample rows for demos; user can hide them. */
  const [realInboxEmptyOrUnavailable, setRealInboxEmptyOrUnavailable] =
    useState(false);
  /** When real inbox is empty/unavailable: show sample actions (default on) vs “all caught up” empty UI. */
  const [showSampleActions, setShowSampleActions] = useState(true);
  /** True when pending-approvals API returned [] or failed (eligible to show mock governance rows in demo mode). */
  const [realApprovalsEmptyOrUnavailable, setRealApprovalsEmptyOrUnavailable] =
    useState(false);
  /** Dismissed sample governance ids (approve/reject on demo rows). */
  const [dismissedSampleApprovalIds, setDismissedSampleApprovalIds] = useState(
    [],
  );

  // Load board actions from GET /api/admin-dash/board-actions/my/. If API returns no rows or errors, fill sample data for demos; toggle hides samples to see the real empty state.
  useEffect(() => {
    const load = async () => {
      setLoadingActions(true);
      try {
        const data = await fetchBoardActions();
        const list = Array.isArray(data) ? data : [];
        if (list.length > 0) {
          setRealInboxEmptyOrUnavailable(false);
          setShowSampleActions(true);
          setDismissedSampleApprovalIds([]);
          setActions(
            list.map((item) => ({
              id: String(item.id),
              type: item.action_type || item.type,
              title: item.title,
              description: item.description || "",
              assigneeId: String(item.assignee_id || currentUserId),
              dueDate: item.due_date || item.dueDate,
              status: item.status || "pending",
              priority: item.priority || "medium",
              estimatedMinutes:
                item.estimated_minutes ||
                getEstimatedTime(item.action_type || item.type),
              surveyType: item.survey_type || item.surveyType,
              surveyRunId: item.survey_run_id || item.surveyRunId,
              category:
                item.category ||
                getActionCategory(item.action_type || item.type),
              pulseRequestId: item.pulse_request_id
                ? String(item.pulse_request_id)
                : undefined,
              meetingId: toNullableNumber(item.meeting_id),
              chemistryNotificationId: toNullableNumber(
                item.chemistry_notification_id,
              ),
              attentionItemId: toNullableNumber(item.attention_item_id),
            }))
          );
        } else {
          setRealInboxEmptyOrUnavailable(true);
          setShowSampleActions(true);
          setDismissedSampleApprovalIds([]);
          setActions(buildMockActions(currentUserId));
        }
      } catch {
        setRealInboxEmptyOrUnavailable(true);
        setShowSampleActions(true);
        setDismissedSampleApprovalIds([]);
        setActions(buildMockActions(currentUserId));
      } finally {
        setLoadingActions(false);
      }
    };
    load();
  }, [currentUserId]);

  // Load pending approvals for real governance UI; empty/error also enables sample governance rows in demo mode.
  useEffect(() => {
    const load = async () => {
      setLoadingApprovals(true);
      try {
        const data = await getPendingApprovals();
        const list = Array.isArray(data)
          ? data
          : data?.results ?? data?.data ?? [];
        setRealApprovalsEmptyOrUnavailable(list.length === 0);
        setApprovalRequests(
          list.map((item) => {
            const roleRaw = (item.role || item.board_role || "").toString().toLowerCase();
            const isAdmin =
              roleRaw === "admin" ||
              roleRaw === "administrator" ||
              roleRaw === "access-manager";
            return {
              id: String(item.id),
              name:
                item.name ||
                item.full_name ||
                `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
                item.email,
              email: item.email,
              image: item.profile_image || item.image || item.avatar,
              accessType: isAdmin ? "administrator" : "member",
              requestedAt: new Date(
                item.requested_at || item.invited_at || item.created_at || Date.now(),
              ),
              accessManagerApproved:
                item.access_manager_approved ?? item.accessManagerApproved ?? true,
            };
          }),
        );
      } catch {
        setRealApprovalsEmptyOrUnavailable(true);
        setApprovalRequests([]);
      } finally {
        setLoadingApprovals(false);
      }
    };
    load();
  }, [currentUserRole]);

  useEffect(() => {
    if (showSampleActions) setDismissedSampleApprovalIds([]);
  }, [showSampleActions]);

  // ─── Filtering & sorting ────────────────────────────────────────────────

  const isSampleActionsMode =
    realInboxEmptyOrUnavailable && showSampleActions;

  const filterActionsByUser = (allActions, role, userId) => {
    if (isSampleActionsMode) return allActions;
    return allActions.filter((action) => {
      if (
        action.type === "review-board-results" ||
        action.type === "pulse-request"
      ) {
        return isChair(role);
      }
      return action.assigneeId === userId;
    });
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const days = Math.ceil((date.getTime() - now.getTime()) / 86400000);
    if (days < 0)
      return { text: "Overdue", urgent: true, isOverdue: true, dueSoon: false };
    if (days === 0)
      return {
        text: "Due today",
        urgent: true,
        isOverdue: false,
        dueSoon: true,
      };
    if (days === 1)
      return {
        text: "Due tomorrow",
        urgent: true,
        isOverdue: false,
        dueSoon: true,
      };
    if (days <= 5)
      return {
        text: `Due in ${days} days`,
        urgent: false,
        isOverdue: false,
        dueSoon: true,
      };
    return {
      text: date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      }),
      urgent: false,
      isOverdue: false,
      dueSoon: false,
    };
  };

  const sortActionsByPriority = (arr) =>
    [...arr].sort((a, b) => {
      const aDue = formatDueDate(a.dueDate);
      const bDue = formatDueDate(b.dueDate);
      if (aDue?.isOverdue && !bDue?.isOverdue) return -1;
      if (!aDue?.isOverdue && bDue?.isOverdue) return 1;
      if (aDue?.isOverdue && bDue?.isOverdue)
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      if (aDue?.dueSoon && !bDue?.dueSoon) return -1;
      if (!aDue?.dueSoon && bDue?.dueSoon) return 1;
      if (aDue?.dueSoon && bDue?.dueSoon)
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      const po = { high: 0, medium: 1, low: 2 };
      const diff =
        (po[a.priority ?? "medium"] ?? 1) - (po[b.priority ?? "medium"] ?? 1);
      if (diff !== 0) return diff;
      if (a.dueDate && b.dueDate)
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      return 0;
    });

  const actionsForDisplay =
    realInboxEmptyOrUnavailable && !showSampleActions ? [] : actions;

  const visibleActions = filterActionsByUser(
    actionsForDisplay,
    currentUserRole,
    currentUserId
  );
  const pendingActions = sortActionsByPriority(
    visibleActions.filter((a) => a.status !== "completed")
  );
  const completedActions = filterActionsByUser(
    actionsForDisplay,
    currentUserRole,
    currentUserId,
  ).filter((a) => a.status === "completed");

  const showGovernanceSamples =
    realApprovalsEmptyOrUnavailable &&
    realInboxEmptyOrUnavailable &&
    showSampleActions;

  const approvalRequestsForDisplay = showGovernanceSamples
    ? buildMockApprovalRequests().filter(
        (r) => !dismissedSampleApprovalIds.includes(r.id),
      )
    : canSeeGovernanceMemberApprovals(currentUserRole)
      ? approvalRequests
      : [];

  const pendingApprovalsCount = approvalRequestsForDisplay.length;
  const totalPending = pendingActions.length + pendingApprovalsCount;
  const hasPendingActions = totalPending > 0;

  // ─── Icon helpers ───────────────────────────────────────────────────────

  const getActionIcon = (type) => {
    const map = {
      "governance-pulse": <HeartPulse className="w-4 h-4" />,
      chemistry: <Users className="w-4 h-4" />,
      "agenda-review": <ClipboardList className="w-4 h-4" />,
      voting: <Vote className="w-4 h-4" />,
      signature: <FileSignature className="w-4 h-4" />,
      "survey-response": <ClipboardList className="w-4 h-4" />,
      "review-board-results": <BarChart3 className="w-4 h-4" />,
      "pulse-request": <ClipboardCheck className="w-4 h-4" />,
    };
    return map[type] ?? <Circle className="w-4 h-4" />;
  };

  const getStatusIcon = (status) => {
    if (status === "completed")
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (status === "in-progress")
      return <Clock className="w-4 h-4 text-amber-500" />;
    return <Circle className="w-4 h-4 text-muted-foreground" />;
  };

  const getPriorityColor = (priority) => {
    const map = {
      high: "bg-red-500/10 text-red-600 border-red-200",
      medium: "bg-amber-500/10 text-amber-600 border-amber-200",
      low: "bg-green-500/10 text-green-600 border-green-200",
    };
    return map[priority] ?? "bg-muted text-muted-foreground";
  };

  const getActionTypeLabel = (type) => {
    const map = {
      "governance-pulse": "Assessment",
      chemistry: "Assessment",
      "agenda-review": "Meeting",
      voting: "Voting",
      signature: "Signature",
      "survey-response": "Survey",
      "review-board-results": "Review",
      "pulse-request": "Anmodning",
    };
    return map[type] ?? "Action";
  };

  const getCategoryLabel = (category) => {
    const map = {
      decisions: "Decisions & votes",
      assessments: "Assessments",
      meetings: "Meetings & documents",
      approvals: "Approvals",
    };
    return map[category] ?? "Actions";
  };

  const getTimeAgo = (date) => {
    const diff = Date.now() - date.getTime();
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return "Just now";
  };

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleActionClick = (action) => {
    if (
      action.type === "review-board-results" &&
      action.surveyType &&
      action.surveyRunId
    ) {
      onNavigateToChairSummary?.(action.surveyType, action.surveyRunId);
      return;
    }
    if (action.type === "pulse-request" && action.pulseRequestId) {
      setSelectedPulseRequest(action.pulseRequestId);
      setPulseRequestDialogOpen(true);
      return;
    }

    if (action.meetingId != null) {
      setSessionMeetingFocus(
        action.meetingId,
        meetingFolderForActionType(action.type),
      );
      dispatchBusinessDashNavigate({ tab: "meeting" });
      return;
    }

    if (action.type === "chemistry" && action.chemistryNotificationId != null) {
      setSessionChemistryNotificationId(action.chemistryNotificationId);
      dispatchBusinessDashNavigate({
        tab: "governance",
        governanceSection: "chemistry",
        openChemistryAssessment: true,
      });
      return;
    }

    if (action.type === "survey-response" && action.attentionItemId != null) {
      setSessionStrategyAttentionId(action.attentionItemId);
      dispatchBusinessDashNavigate({ tab: "strategi" });
      return;
    }

    switch (action.type) {
      case "governance-pulse":
        onNavigateToGovernance?.("pulse", false);
        break;
      case "chemistry":
      case "survey-response":
        onNavigateToGovernance?.("chemistry", false);
        break;
      case "agenda-review":
      case "voting":
      case "signature":
        onNavigateToMeeting?.();
        break;
      default:
        break;
    }
  };

  const handlePulseRequestDecision = async (decision, postponeDays) => {
    if (selectedPulseRequest) {
      try {
        await decidePulseRequest(selectedPulseRequest, decision, postponeDays);
      } catch {
        // Log silently — remove locally regardless
      }
      setActions((prev) =>
        prev.filter((a) => a.pulseRequestId !== selectedPulseRequest)
      );
      onPulseRequestDecision?.(selectedPulseRequest, decision, postponeDays);
    }
    setPulseRequestDialogOpen(false);
    setSelectedPulseRequest(null);
  };

  const handleMarkAsReviewed = async (actionId, e) => {
    e.stopPropagation();
    setActions((prev) =>
      prev.map((a) =>
        a.id === actionId ? { ...a, status: "completed" } : a
      )
    );
    onMarkResultsReviewed?.(actionId);
    try {
      await markBoardActionComplete(actionId);
    } catch {
      // Optimistic update is fine
    }
  };

  const isSampleGovernanceApprovalId = (id) =>
    String(id).startsWith("sample-gov-");

  const handleApproveRequest = async (request) => {
    if (isSampleGovernanceApprovalId(request.id)) {
      onMemberApproved?.({
        name: request.name,
        email: request.email,
        image: request.image,
      });
      setDismissedSampleApprovalIds((prev) => [...prev, request.id]);
      return;
    }
    try {
      await approveBoardMember(request.id);
      onMemberApproved?.({
        name: request.name,
        email: request.email,
        image: request.image,
      });
    } catch {
      // Still remove from UI
    }
    setApprovalRequests((prev) => prev.filter((r) => r.id !== request.id));
  };

  const openRejectDialog = (request) => {
    setRejectTarget(request);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget?.id) return;
    if (!rejectionReason.trim()) return;
    if (isSampleGovernanceApprovalId(rejectTarget.id)) {
      setDismissedSampleApprovalIds((prev) => [...prev, rejectTarget.id]);
    } else {
      try {
        await rejectBoardMember(rejectTarget.id, {
          reason: rejectionReason.trim(),
        });
      } catch {
        // Still remove from UI
      }
      setApprovalRequests((prev) =>
        prev.filter((r) => r.id !== rejectTarget.id),
      );
    }
    setRejectDialogOpen(false);
    setRejectTarget(null);
    setRejectionReason("");
  };

  // ─── Grouped actions ────────────────────────────────────────────────────

  const groupedActions = pendingActions.reduce((acc, action) => {
    const cat = action.category || getActionCategory(action.type);
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(action);
    return acc;
  }, {});

  const categoryOrder = ["decisions", "assessments", "meetings"];

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-muted/30">
      {/* Collapsible header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/10 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" />
          <span className="text-base font-semibold">Your Board Actions</span>
          {hasPendingActions && (
            <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full min-w-[20px] text-center animate-pulse">
              {totalPending}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </Button>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <CardContent className="pt-0 space-y-3">
          <p className="text-xs text-muted-foreground">
            Personal tasks requiring your attention
          </p>

          {realInboxEmptyOrUnavailable && showSampleActions && (
            <div className="rounded-md border border-dashed border-primary/25 bg-primary/5 px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground/90">
                  Sample actions & governance approvals
                </span>{" "}
                — your inbox is empty (or the request failed). Showing demo
                rows (including Governance Approvals) so you can test the panel.
                Use the link to see the real empty state.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSampleActions(false);
                }}
              >
                Show empty inbox
              </Button>
            </div>
          )}

          {realInboxEmptyOrUnavailable && !showSampleActions && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSampleActions(true);
                }}
              >
                Show sample actions for testing
              </Button>
            </div>
          )}

          {loadingActions ? (
            <div className="py-6 text-center">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Loading actions…</p>
            </div>
          ) : !loadingActions && loadingApprovals ? (
            <div className="py-6 text-center">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                Loading governance approvals…
              </p>
            </div>
          ) : pendingActions.length === 0 && pendingApprovalsCount === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-sm font-medium">You're all caught up.</p>
              <p className="text-xs mt-1">No pending actions at this time.</p>
            </div>
          ) : (
            <>
              {/* Pending actions by category */}
              <div className="space-y-4">
                {categoryOrder.map((category) => {
                  const categoryActions = groupedActions[category];
                  if (!categoryActions?.length) return null;
                  return (
                    <div
                      key={category}
                      className="space-y-2 mt-10 border-t border-border/50 pt-6 first:mt-5 first:border-t-0 first:pt-0"
                    >
                      <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#09366b] dark:text-sky-300">
                        {getCategoryLabel(category)}
                      </h4>
                      {categoryActions.map((action) => {
                        const dueInfo = formatDueDate(action.dueDate);
                        const isReviewAction =
                          action.type === "review-board-results";
                        return (
                          <div
                            key={action.id}
                            className="group flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-muted/30 hover:border-primary/30 hover:bg-background transition-all cursor-pointer"
                            onClick={() => handleActionClick(action)}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <div
                                className={cn(
                                  "w-8 h-8 rounded-full flex items-center justify-center",
                                  isReviewAction
                                    ? "bg-primary/10 text-primary"
                                    : action.status === "in-progress"
                                    ? "bg-amber-500/10 text-amber-600"
                                    : "bg-primary/10 text-primary"
                                )}
                              >
                                {getActionIcon(action.type)}
                              </div>
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                                    {action.title}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                    {action.description}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] px-1.5 py-0 h-5 bg-muted/50 text-muted-foreground border-muted"
                                  >
                                    {getActionTypeLabel(action.type)}
                                  </Badge>
                                  {action.priority && (
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "text-[10px] px-1.5 py-0 h-5",
                                        getPriorityColor(action.priority)
                                      )}
                                    >
                                      {action.priority}
                                    </Badge>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {getStatusIcon(action.status)}
                                <span className="text-xs text-muted-foreground capitalize">
                                  {action.status.replace("-", " ")}
                                </span>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />~
                                  {action.estimatedMinutes} min
                                </span>
                                {dueInfo && (
                                  <>
                                    <span className="text-muted-foreground">
                                      •
                                    </span>
                                    <span
                                      className={cn(
                                        "text-xs",
                                        dueInfo.urgent
                                          ? "text-red-500 font-medium"
                                          : "text-muted-foreground"
                                      )}
                                    >
                                      {dueInfo.text}
                                    </span>
                                  </>
                                )}
                              </div>

                              {isReviewAction && (
                                <div className="flex items-center gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleActionClick(action);
                                    }}
                                  >
                                    <BarChart3 className="w-3 h-3 mr-1" />
                                    View Chair Summary
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                                    onClick={(e) =>
                                      handleMarkAsReviewed(action.id, e)
                                    }
                                  >
                                    <CheckSquare className="w-3 h-3 mr-1" />
                                    Mark as reviewed
                                  </Button>
                                </div>
                              )}
                            </div>

                            {!isReviewAction && (
                              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-2" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Governance Approvals — real list for authorized roles; sample rows for everyone in demo mode */}
              {(showGovernanceSamples ||
                canSeeGovernanceMemberApprovals(currentUserRole)) && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-2 mt-8 pt-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#09366b] dark:text-sky-300" />
                      <h4 className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#09366b] dark:text-sky-300">
                        Governance Approvals
                      </h4>
                      {approvalRequestsForDisplay.length > 0 && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {approvalRequestsForDisplay.length}
                        </span>
                      )}
                    </div>

                    {loadingApprovals ? (
                      <div className="py-4 text-center">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                      </div>
                    ) : approvalRequestsForDisplay.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground bg-background/30 rounded-lg">
                        <p className="text-xs">No approvals pending.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {approvalRequestsForDisplay.map((request) => (
                          <div
                            key={request.id}
                            className="bg-background/50 rounded-lg p-3 border border-muted/30"
                          >
                            <div className="flex items-start gap-2.5">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={request.image} />
                                <AvatarFallback className="text-xs">
                                  {request.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-xs font-medium text-foreground truncate">
                                    {request.name}
                                  </p>
                                  <span
                                    className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                                      request.accessType === "administrator"
                                        ? "bg-amber-500/20 text-amber-600"
                                        : "bg-blue-500/20 text-blue-600"
                                    }`}
                                  >
                                    {request.accessType === "administrator"
                                      ? "Admin"
                                      : "Member"}
                                  </span>
                                </div>
                                <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                                  {getTimeAgo(request.requestedAt)}
                                </p>
                                <div className="mt-2 mb-2">
                                  {request.accessManagerApproved ? (
                                    <span className="text-[10px] text-green-600 bg-green-500/10 px-2 py-0.5 rounded-full">
                                      ✓ Ready for your approval
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full">
                                      Waiting for Access Manager
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2 mt-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="h-6 text-[10px] flex-1"
                                    disabled={!request.accessManagerApproved}
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Approve
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-background">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Approve Board Access
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Grant{" "}
                                      <strong>{request.name}</strong> access as{" "}
                                      {request.accessType === "administrator"
                                        ? "an Administrator"
                                        : "a Board Member"}
                                      ?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() =>
                                        handleApproveRequest(request)
                                      }
                                    >
                                      Approve
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>

                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-[10px] flex-1"
                                disabled={!request.accessManagerApproved}
                                onClick={() => openRejectDialog(request)}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Summary footer */}
              <div className="pt-2 border-t border-muted/20">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {pendingActions.length} action
                    {pendingActions.length !== 1 ? "s" : ""} pending
                  </span>
                  {completedActions.length > 0 && (
                    <span className="text-green-600">
                      {completedActions.length} completed
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      )}

      {/* Pulse Request Approval Dialog */}
      {selectedPulseRequest && (
        <PulseRequestApproval
          request={{
            id: selectedPulseRequest,
            createdAt: new Date().toISOString(),
            cadence: "annual",
            status: "pending",
          }}
          isOpen={pulseRequestDialogOpen}
          onClose={() => {
            setPulseRequestDialogOpen(false);
            setSelectedPulseRequest(null);
          }}
          onDecision={handlePulseRequestDecision}
        />
      )}

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject board access request</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting{" "}
              <strong>{rejectTarget?.name}</strong>&apos;s request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="governance-reject-reason">Reason</Label>
            <Textarea
              id="governance-reject-reason"
              placeholder="e.g. conflict of interest, incomplete information…"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectTarget(null);
                setRejectionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!rejectionReason.trim()}
              onClick={handleRejectConfirm}
            >
              Reject request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BoardActionsPanel;
